import React, { useEffect, useState, ChangeEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminGuard from '../components/AdminGuard';
import AdminLayout from '../components/AdminLayout';
import { api } from '../../../api/client';

type NotificationType = 'alert' | 'transition' | 'liveactivity_update' | 'liveactivity_end';

interface User {
  displayName?: string;
  email: string;
}

interface Timeline {
  name: string;
}

interface NotificationLog {
  id: string;
  type: NotificationType;
  user?: User;
  timeline?: Timeline;
  success: boolean;
  errorMessage?: string;
  payload: Record<string, unknown>;
  apnsResponse?: Record<string, unknown>;
  deviceToken: string;
  createdAt: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface Filters {
  type: string;
  success: string;
  userId: string;
}

interface NotificationLogsResponse {
  logs: NotificationLog[];
  pagination: Pagination;
}

const AdminNotificationsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [logs, setLogs] = useState<NotificationLog[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // Filter state
  const [filters, setFilters] = useState<Filters>({
    type: searchParams.get('type') || '',
    success: searchParams.get('success') || '',
    userId: searchParams.get('userId') || '',
  });

  const fetchLogs = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: String(page), limit: '50' });
      if (filters.type) params.append('type', filters.type);
      if (filters.success) params.append('success', filters.success);
      if (filters.userId) params.append('userId', filters.userId);

      const response = await api.get(`/admin/notifications?${params}`) as NotificationLogsResponse;
      setLogs(response.logs);
      setPagination(response.pagination);
    } catch (err) {
      const apiError = err as { message?: string };
      setError(apiError.message || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (key: keyof Filters, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);

    // Update URL params
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    setSearchParams(params);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getTypeBadge = (type: NotificationType) => {
    const styles: Record<NotificationType, string> = {
      alert: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      transition: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      liveactivity_update: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      liveactivity_end: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    };
    const labels: Record<NotificationType, string> = {
      alert: 'Alert',
      transition: 'Transition',
      liveactivity_update: 'LA Update',
      liveactivity_end: 'LA End',
    };
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded ${styles[type] || styles.alert}`}>
        {labels[type] || type}
      </span>
    );
  };

  return (
    <AdminGuard>
      <AdminLayout>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Push Notification Logs</h2>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {pagination.total} total logs
            </span>
          </div>

          {/* Filters */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
            <div className="flex flex-wrap gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Type
                </label>
                <select
                  value={filters.type}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => handleFilterChange('type', e.target.value)}
                  className="block w-40 px-3 py-2 text-sm border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">All types</option>
                  <option value="alert">Alert</option>
                  <option value="transition">Transition</option>
                  <option value="liveactivity_update">LA Update</option>
                  <option value="liveactivity_end">LA End</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  value={filters.success}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => handleFilterChange('success', e.target.value)}
                  className="block w-40 px-3 py-2 text-sm border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">All</option>
                  <option value="true">Success</option>
                  <option value="false">Failed</option>
                </select>
              </div>
              {filters.userId && (
                <div className="flex items-end">
                  <button
                    onClick={() => handleFilterChange('userId', '')}
                    className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300"
                  >
                    Clear user filter
                  </button>
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 p-4 rounded-lg">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500 dark:text-gray-400">Loading...</div>
            </div>
          ) : (
            <>
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Timeline
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {logs.map((log) => (
                      <React.Fragment key={log.id}>
                        <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(log.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getTypeBadge(log.type)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {log.user ? (
                              <div>
                                <div className="text-sm text-gray-900 dark:text-white">
                                  {log.user.displayName || log.user.email}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {log.user.email}
                                </div>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {log.timeline?.name || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {log.success ? (
                              <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 rounded">
                                Success
                              </span>
                            ) : (
                              <div>
                                <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 rounded">
                                  Failed
                                </span>
                                {log.errorMessage && (
                                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                                    {log.errorMessage}
                                  </p>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                              className="text-blue-600 hover:text-blue-700 dark:text-blue-400"
                            >
                              {expandedRow === log.id ? 'Hide' : 'Details'}
                            </button>
                          </td>
                        </tr>
                        {expandedRow === log.id && (
                          <tr>
                            <td colSpan={6} className="px-6 py-4 bg-gray-50 dark:bg-gray-900">
                              <div className="space-y-4">
                                <div>
                                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Payload
                                  </h4>
                                  <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-3 rounded overflow-x-auto">
                                    {JSON.stringify(log.payload, null, 2)}
                                  </pre>
                                </div>
                                {log.apnsResponse && (
                                  <div>
                                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                      APNs Response
                                    </h4>
                                    <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-3 rounded overflow-x-auto">
                                      {JSON.stringify(log.apnsResponse, null, 2)}
                                    </pre>
                                  </div>
                                )}
                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Device Token: {log.deviceToken.substring(0, 20)}...
                                  </p>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => fetchLogs(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => fetchLogs(pagination.page + 1)}
                    disabled={pagination.page === pagination.totalPages}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </AdminLayout>
    </AdminGuard>
  );
};

export default AdminNotificationsPage;
